import { TFunction } from "i18next";
import { isOnlinePaymentTitle } from "../../checkout/libs/paymentMethods";

/**
 * Статусы заказа (`last_status_id`) по справочнику бэкенда:
 *
 *  0 начат · 1 подтверждён · 2 отменён · 3 получен в раздаче · 4 упакован в
 *  раздаче · 5 передан клиенту · 6 клиент вызван · 7 удалён · 8 курьер
 *  забрал · 9 оплачен · 10 ожидание оплаты · 11 получен в ПБО · 12
 *  переоформление · 13 выбран другой ресторан · 14 аннулирование · 15
 *  предварительный · 16 ошибка обработки · 17 фискализируется · 18 проверка
 *  привязки курьера · 19 курьер прибыл в ПБО · 20 курьер приехал по локации ·
 *  21 отложен
 *
 * `last_status_title` при этом гуляет, поэтому опираемся только на id.
 */
export const ORDER_STATUS_ID = {
  CREATED: 0,
  ACCEPTED: 1,
  CANCELLED: 2,
  RECEIVED_AT_SERVING_AREA: 3,
  PACKED_AT_SERVING_AREA: 4,
  HANDED_TO_CUSTOMER: 5,
  DELETED: 7,
  COURIER_PICKED_UP: 8,
  PAID: 9,
  RECEIVED_AT_PICKUP_POINT: 11,
  ANNULLED: 14,
  COURIER_ARRIVED: 20,
} as const;

/** `last_payment_status_id`: платёж заведён, деньги ещё не пришли. */
const PAYMENT_STATUS_STARTED = 1;

/** `last_payment_type_id` онлайн-оплаты. */
const ONLINE_PAYMENT_TYPE_ID = 60;

/** Поля заказа, по которым определяется статус; есть и в списке, и в деталях. */
interface OrderStatusSource {
  last_status_id: number;
  last_status_title: string;
  last_payment_status_id: number;
  last_payment_title: string;
  last_payment_type_id: number;
  paid: string;
}

/**
 * Известен ли способ оплаты. В постраничном списке (`/user/orders-paginated`)
 * бэкенд отдаёт урезанный заказ: из платёжного там только
 * `last_payment_status_id`, а `last_payment_type_id`, `last_payment_title` и
 * `paid` отсутствуют. Отличить онлайн от наличных по такому ответу нельзя.
 */
const hasPaymentTypeInfo = (order: OrderStatusSource) =>
  order.last_payment_type_id > 0 || Boolean(order.last_payment_title);

/**
 * Оплата по ссылке — по типу платежа или, если его нет, по подписи.
 *
 * Когда способ неизвестен вовсе (тот самый урезанный список), считаем его
 * подходящим: «заказ начат, платёж начат, ничего не оплачено» — это и есть
 * ожидание оплаты, иначе такой заказ показывался бы в списке как «В
 * процессе», хотя на его экране написано «Ожидает оплаты».
 */
const isOnlinePayment = (order: OrderStatusSource) =>
  !hasPaymentTypeInfo(order) ||
  order.last_payment_type_id === ONLINE_PAYMENT_TYPE_ID ||
  isOnlinePaymentTitle(order.last_payment_title);

/**
 * Заказ создан, но онлайн-оплата ещё не прошла: статус «Заказ начат» (0),
 * платёж «начат» и ничего не оплачено. Наличные сюда не попадают — у них
 * тот же нулевой статус, но заказ уже в работе, ждать от клиента нечего.
 */
export const isAwaitingPayment = (order: OrderStatusSource) =>
  order.last_status_id === ORDER_STATUS_ID.CREATED &&
  order.last_payment_status_id === PAYMENT_STATUS_STARTED &&
  Number(order.paid) === 0 &&
  isOnlinePayment(order);

/** Этап заказа для карточки в списке — сводит два десятка статусов к шести. */
export type OrderStage =
  | "inProgress"
  | "preparing"
  | "delivering"
  | "completed"
  | "cancelled"
  | "unknown";

/** Шаг трекера в деталях заказа: 1 — принят, 2 — готовится, 3 — курьер, 4 — получен. */
export type OrderProgressStep = 1 | 2 | 3 | 4;

export const ORDER_PROGRESS_STEPS = 4;

/**
 * Единственная таблица статусов: и подпись в списке, и шаг трекера берутся
 * отсюда, чтобы они не разъезжались.
 *
 * `stage` — подпись на карточке в списке, `step` — сколько кружков трекера
 * горит. Они не всегда совпадают один к одному: подписей шесть, кружков
 * четыре. Например «получен в раздаче» (3) — это ещё «В процессе» в списке,
 * но на трекере уже «готовится»: заказ у поваров.
 */
const ORDER_STATUS_MAP: Record<
  number,
  { stage: OrderStage; step: OrderProgressStep }
> = {
  0: { stage: "inProgress", step: 1 }, // заказ начат
  1: { stage: "inProgress", step: 1 }, // подтверждён
  9: { stage: "inProgress", step: 1 }, // оплачен
  10: { stage: "inProgress", step: 1 }, // ожидание оплаты
  12: { stage: "inProgress", step: 1 }, // переоформление
  13: { stage: "inProgress", step: 1 }, // выбран другой ресторан
  15: { stage: "inProgress", step: 1 }, // предварительный заказ
  16: { stage: "inProgress", step: 1 }, // ошибка в обработке
  3: { stage: "inProgress", step: 2 }, // получен в раздаче — готовят
  17: { stage: "inProgress", step: 2 }, // фискализируется
  4: { stage: "preparing", step: 2 }, // упакован в раздаче
  8: { stage: "delivering", step: 3 }, // курьер забрал
  18: { stage: "inProgress", step: 3 }, // проверка привязки курьера
  19: { stage: "delivering", step: 3 }, // курьер прибыл в ПБО
  20: { stage: "delivering", step: 3 }, // курьер приехал по локации
  5: { stage: "completed", step: 4 }, // передан клиенту
  11: { stage: "completed", step: 4 }, // получен в ПБО — самовывоз забрали
  2: { stage: "cancelled", step: 1 }, // отменён
  7: { stage: "cancelled", step: 1 }, // удалён
  14: { stage: "cancelled", step: 1 }, // аннулирован
  6: { stage: "unknown", step: 3 }, // клиент вызван — заказ на выдаче
  // 21 «отложен» и всё незнакомое (в т.ч. -1) — unknown / первый шаг.
};

export const getOrderStage = (statusId: number): OrderStage =>
  ORDER_STATUS_MAP[statusId]?.stage ?? "unknown";

export const isOrderCancelled = (statusId: number) =>
  getOrderStage(statusId) === "cancelled";

/**
 * Отменённый заказ шага не имеет — для него трекер не рисуется; незнакомый
 * статус — «принят»: заказ существует, дальше ничего не знаем.
 */
export const getOrderProgressStep = (statusId: number): OrderProgressStep =>
  ORDER_STATUS_MAP[statusId]?.step ?? 1;

/**
 * Заказ у клиента: еду отдали в руки (5) или забрали из ресторана (11).
 * Только с этого момента есть что оценивать — «курьер приехал» (20) значит,
 * что он ещё у двери.
 */
export const isOrderHandedOver = (statusId: number) =>
  statusId === ORDER_STATUS_ID.HANDED_TO_CUSTOMER ||
  statusId === ORDER_STATUS_ID.RECEIVED_AT_PICKUP_POINT;

/** Ключ текста статуса в деталях заказа. */
const DETAIL_TEXT_BY_STATUS: Record<number, string> = {
  0: "orderAccepted",
  1: "orderAccepted",
  2: "orderCanceled",
  3: "receivedAtServingArea",
  4: "packedAtServingArea",
  5: "handedToCustomer",
  8: "courierPickedUpOrder",
  9: "orderAccepted",
  20: "courierArrivedAtLocation",
};

/** Заголовок статуса в деталях заказа; для незнакомого id — «Ваш заказ принят». */
export const getOrderStatusTitle = (t: TFunction, order: OrderStatusSource) => {
  if (isAwaitingPayment(order)) return t("orderStatusTitleAwaitingPayment");

  return t(
    `orderStatusText.${DETAIL_TEXT_BY_STATUS[order.last_status_id] ?? "orderAccepted"}`,
  );
};

interface OrderStatusBadge {
  label: string;
  className: string;
  /** Заказ ждёт онлайн-оплату: карточка рисует часы, как и экран заказа. */
  awaitingPayment: boolean;
}

const STAGE_CLASS_NAME: Record<OrderStage, string> = {
  inProgress: "text-[#FF9900]",
  preparing: "text-[#FF9900]",
  delivering: "text-[#FF9900]",
  completed: "text-brand-green",
  cancelled: "text-red-500",
  unknown: "text-text-tertiary",
};

/** Короткий статус для карточки в списке заказов: подпись и цвет. */
export const getOrderStatusBadge = (
  t: TFunction,
  order: OrderStatusSource,
): OrderStatusBadge => {
  if (isAwaitingPayment(order)) {
    return {
      label: t("orderStatus.awaitingPayment"),
      className: "text-[#FF9900]",
      awaitingPayment: true,
    };
  }

  const stage = getOrderStage(order.last_status_id);

  return {
    label: t(`orderStatus.${stage}`),
    className: STAGE_CLASS_NAME[stage],
    awaitingPayment: false,
  };
};
